import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, User, MapPin, Phone, Heart } from "@/lib/icons";
import CategoryMultiSelect, { selectedCategories } from "@/components/CategoryMultiSelect";

export const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const PREFERRED = [
  { value: "phone", label: "Phone Call" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "telegram", label: "Telegram" },
  { value: "any", label: "Any Method" },
];

const inputCls = "h-11 bg-white/5 border-white/10 text-white placeholder:text-white/35";
const textareaCls = "min-h-[90px] bg-white/5 border-white/10 text-white placeholder:text-white/35 text-sm";

/**
 * Member-facing slide-in editor for the fields collected after registration
 * (studio/category/location/contact/blood group). Saves via PATCH /member/profile.
 */
export default function EditProfilePanel({ open, onOpenChange, user, onSaved }) {
  const { settings } = useSettings();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        full_name: user.full_name || "",
        studio_name: user.studio_name || "",
        category: user.category || "",
        categories: selectedCategories(user),
        state: user.state || "",
        city: user.city || "",
        area: user.area || "",
        pincode: user.pincode || "",
        address: user.address || "",
        bio: user.bio || "",
        website: user.website || "",
        facebook_url: user.facebook_url || "",
        instagram_url: user.instagram_url || "",
        experience_years: user.experience_years ?? "",
        blood_group: user.blood_group || "",
        whatsapp_number: user.whatsapp_number || "",
        telegram_username: user.telegram_username || "",
        telegram_number: user.telegram_number || "",
        preferred_contact_method: user.preferred_contact_method || "any",
      });
    }
  }, [open, user]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const categories = (settings?.categories || []).filter((c) => c && c.enabled !== false && c.name);
  const locationNames = (items) => (items || [])
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(Boolean);
  const stateOptions = locationNames(settings?.locations?.states);
  const cityOptions = locationNames(settings?.locations?.cities);

  const save = async () => {
    if (!form.full_name.trim()) return toast.error("Full name can't be empty");
    if (!form.categories.length || !form.category) return toast.error("Select at least one category and choose a primary category");
    setBusy(true);
    try {
      const { data } = await api.patch("/member/profile", {
        ...form,
        experience_years: form.experience_years === "" ? null : Number(form.experience_years),
      });
      toast.success("Profile updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto bg-ink-900 border-l border-white/10 text-white" data-testid="edit-profile-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-3xl text-white">Edit profile</SheetTitle>
          <SheetDescription className="text-white/55">
            Studio, category and location details shown on your public profile and Pro Card.
          </SheetDescription>
        </SheetHeader>

        {!form ? (
          <div className="py-16 text-center text-white/40">Loading…</div>
        ) : (
          <div className="mt-6 space-y-8">
            <Section icon={User} title="Basic info">
              <Field label="Full name" required>
                <Input value={form.full_name} onChange={(e) => set("full_name")(e.target.value)} className={inputCls} data-testid="edit-full-name" />
              </Field>
              <Field label="Studio / business name">
                <Input value={form.studio_name} onChange={(e) => set("studio_name")(e.target.value)} className={inputCls} data-testid="edit-studio-name" />
              </Field>
              <Field label="Categories" required span="full">
                <CategoryMultiSelect
                  options={categories.length ? categories : [{ name: "Photographer" }]}
                  categories={form.categories}
                  primary={form.category}
                  onChange={({ categories: next, category }) => setForm((value) => ({ ...value, categories: next, category }))}
                  testid="edit-categories"
                />
              </Field>
              <Field label="Years of experience">
                <Input type="number" min="0" max="80" value={form.experience_years}
                  onChange={(e) => set("experience_years")(e.target.value)} className={inputCls} data-testid="edit-experience" />
              </Field>
              <Field label="Website" span="full">
                <Input value={form.website} onChange={(e) => set("website")(e.target.value)} placeholder="https://…" className={inputCls} data-testid="edit-website" />
              </Field>
              <Field label="Facebook link">
                <Input value={form.facebook_url} onChange={(e) => set("facebook_url")(e.target.value)} placeholder="https://facebook.com/…" className={inputCls} data-testid="edit-facebook" />
              </Field>
              <Field label="Instagram link">
                <Input value={form.instagram_url} onChange={(e) => set("instagram_url")(e.target.value)} placeholder="https://instagram.com/…" className={inputCls} data-testid="edit-instagram" />
              </Field>
              <Field label="Bio" span="full">
                <Textarea value={form.bio} onChange={(e) => set("bio")(e.target.value)} className={textareaCls} data-testid="edit-bio" />
              </Field>
            </Section>

            <Section icon={MapPin} title="Location">
              <Field label="State" required>
                <Input list="edit-state-options" value={form.state} onChange={(e) => set("state")(e.target.value)} className={inputCls} data-testid="edit-state" />
                <datalist id="edit-state-options">{stateOptions.map((s) => <option key={s} value={s} />)}</datalist>
              </Field>
              <Field label="City" required>
                <Input list="edit-city-options" value={form.city} onChange={(e) => set("city")(e.target.value)} className={inputCls} data-testid="edit-city" />
                <datalist id="edit-city-options">{cityOptions.map((s) => <option key={s} value={s} />)}</datalist>
              </Field>
              <Field label="Area / locality">
                <Input value={form.area} onChange={(e) => set("area")(e.target.value)} className={inputCls} data-testid="edit-area" />
              </Field>
              <Field label="Pincode">
                <Input value={form.pincode} onChange={(e) => set("pincode")(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit pincode" className={inputCls} data-testid="edit-pincode" />
              </Field>
              <Field label="Address" span="full">
                <Textarea value={form.address} onChange={(e) => set("address")(e.target.value)} className={textareaCls} rows={2} data-testid="edit-address" />
              </Field>
            </Section>

            <Section icon={Phone} title="Contact preferences">
              <Field label="WhatsApp number">
                <Input value={form.whatsapp_number} onChange={(e) => set("whatsapp_number")(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit number" className={inputCls} data-testid="edit-whatsapp" />
              </Field>
              <Field label="Preferred contact method">
                <Select value={form.preferred_contact_method} onValueChange={set("preferred_contact_method")}>
                  <SelectTrigger className={inputCls} data-testid="edit-preferred"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PREFERRED.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Telegram username">
                <Input value={form.telegram_username} onChange={(e) => set("telegram_username")(e.target.value)} placeholder="@handle" className={inputCls} data-testid="edit-telegram-username" />
              </Field>
              <Field label="Telegram phone (optional)">
                <Input value={form.telegram_number} onChange={(e) => set("telegram_number")(e.target.value.replace(/\D/g, "").slice(0, 10))} className={inputCls} data-testid="edit-telegram-number" />
              </Field>
            </Section>

            <Section icon={Heart} title="Emergency info">
              <Field label="Blood group" required>
                <Select value={form.blood_group || undefined} onValueChange={set("blood_group")}>
                  <SelectTrigger className={inputCls} data-testid="edit-blood-group"><SelectValue placeholder="Select blood group" /></SelectTrigger>
                  <SelectContent>
                    {BLOOD_GROUPS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </Section>

            <div className="pt-4 flex justify-end gap-2 border-t border-white/10">
              <button onClick={() => onOpenChange(false)} className="btn-ghost rounded-full px-5 py-2.5 text-sm font-semibold text-white">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="edit-profile-save"
                className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                <Save size={14} /> {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 font-display text-xl text-white">
        <Icon size={17} className="text-fuchsia-300" /> {title}
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, required, span, children }) {
  return (
    <div className={span === "full" ? "sm:col-span-2" : ""}>
      <label className="overline text-white/60 text-[10px]">
        {label} {required && <span className="text-fuchsia-400">*</span>}
      </label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
